import React, { useEffect, useRef, useState } from 'react';
import MainCard from 'components/MainCard';
import { styled, useTheme } from '@mui/material/styles';
import { AddUserIcon } from 'assets/svgs/index';
import { Box, Typography, Avatar, CircularProgress } from '@mui/material';
import { useParams, useLocation } from 'react-router-dom';
import { apiRequest } from 'helpers/apiRequests';
import { SUPPORT_IMAGE_TYPE, maxFileSizeAllow } from 'constant/AppConfig';
import { toast } from 'react-toastify';
import Form from './components/Form';
import NotFound from 'components/NotFound';
import ImageCropper from 'components/@extended/ImageCropper';

const SVGBox = styled(Box)(({ theme }) => ({
  height: '63px',
  width: '63px',
  background: theme.palette.green.light,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  margin: '0 auto 10px',
  overflow: 'hidden',
  img: {
    height: '100%',
    width: '100%',
    objectFit: 'cover',
    objectPosition: 'top'
    // border: `1px solid ${theme.palette.slate[200]}`,
    // borderRadius: '50%'
  }
}));

const GroupCompanyUserAdd = () => {
  const theme = useTheme();
  const location = useLocation();
  const params = useParams();
  const fileInputRef = useRef(null);
  const [profileImage, setProfileImage] = useState(null);
  const [userData, setUserData] = useState(null);
  const [formDetails, setFormDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpenModal, setOpenModal] = useState(false);
  const [file, setFile] = useState();

  useEffect(() => {
    (async () => {
      try {
        const result = await apiRequest.getTenantUserFormDetails(params?.companyId, params?.userId ? params.userId : '');
        if (result?.data.status === 200) {
          setFormDetails(result?.data?.data);
        }
      } catch (error) {
        console.log('error: ', error);
      }
    })();
  }, [params?.companyId]);

  useEffect(() => {
    if (params?.userId && formDetails) {
      (async () => {
        try {
          const result = await apiRequest.getGroupCompanyUserById(params?.userId);
          if (result?.data.status === 200) {
            setUserData(result?.data?.data);
            setProfileImage(result?.data?.data?.profile_image);
          } else {
            setUserData(undefined);
          }
        } catch (error) {
          setUserData(undefined);
        }
      })();
    }
  }, [params?.userId, formDetails]);

  const handleFileChange = async (e) => {
    try {
      const file = e.target.files;
      if (file) {
        if (!SUPPORT_IMAGE_TYPE.includes(file[0]?.type)) {
          toast.error('Uploaded file is not image!');
          return;
        }
        if (file[0].size >= maxFileSizeAllow * 1024 * 1024) {
          toast.error(`Uploaded image is more than ${maxFileSizeAllow} MB.`);
          fileInputRef.current.value = '';
          return;
        }
        setFile(file[0]);
        setOpenModal(true);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message);
      fileInputRef.current.value = null;
    }
  };

  const handleFileUpload = () => {
    fileInputRef.current.click();
  };

  const handleClose = async (file) => {
    setOpenModal(false);
    try {
      if (file === '') throw Error('');
      setIsLoading(true);
      const response = await apiRequest.uploadProfileImage(file, 'avatar', 'tenant');
      if (response?.data.status === 200) {
        toast.success(response?.data?.message);
        setProfileImage(response?.data?.data?.image_url);
      }
    } catch (error) {
      fileInputRef.current.value = null;
      if (error?.response?.status === 413) {
        toast.error('Uploaded image is more than 5 MB.');
      }
      setFile(null);
      toast.error(error?.response?.data?.message);
    } finally {
      fileInputRef.current.value = null;
      setFile(null);
      setIsLoading(false);
    }
  };

  if (!location?.state && userData !== undefined) {
    return (
      <NotFound
        message={'Company not found!'}
        buttonTitle={'Go Back'}
        path="/companies"
      />
    );
  }

  if (userData === undefined) {
    return (
      <NotFound
        message={'Company user not found!'}
        buttonTitle={'Go Back'}
        path="-1"
      />
    );
  }

  return (
    <MainCard
      content={false}
      sx={{ position: 'relative', maxWidth: '728px', margin: 'auto', borderRadius: '16px', padding: { xs: '15px', sm: '24px 49px 24px' } }}
    >
      <Box sx={{ position: 'absolute', right: '10px', top: '10px' }}>
        <Typography
          sx={{ fontWeight: 500 }}
          variant="subtitle2"
          color="error"
        >
          * Required
        </Typography>
      </Box>
      <Box sx={{ textAlign: 'center' }}>
        <Box
          sx={{ cursor: 'pointer', width: 'fit-content', margin: 'auto' }}
          onClick={handleFileUpload}
        >
          {isLoading ? (
            <SVGBox sx={{ opacity: 0.5, filter: 'brightness(0.5)' }}>
              <CircularProgress
                sx={{ width: '30px !important', height: '30px !important' }}
                color="primary"
              />
            </SVGBox>
          ) : (
            <SVGBox>
              {profileImage?.length > 1 ? (
                <Avatar
                  src={profileImage}
                  alt={`${userData?.firstName?.[0].toUpperCase()} ${userData?.lastName?.[0]?.toUpperCase()}`}
                  sx={{ bgcolor: 'secondary', fontSize: '15px', color: 'black', width: 63, height: 63 }}
                />
              ) : (
                <AddUserIcon stroke={theme.palette.green.main} />
              )}
            </SVGBox>
          )}
        </Box>
        <input
          style={{ display: 'none' }}
          type="file"
          ref={fileInputRef}
          id="fileupload"
          onChange={handleFileChange}
        />
        <Typography
          sx={{ fontWeight: 500 }}
          variant="h3"
        >
          {params?.userId ? 'Edit Group User' : 'Add Group User'}
        </Typography>
      </Box>
      <Box>
        <Form
          formDetails={formDetails}
          companyDetail={location?.state}
          profileImage={profileImage}
          userData={userData}
        />
      </Box>
      <ImageCropper
        image={file}
        isOpenModal={isOpenModal}
        handleClose={handleClose}
      />
    </MainCard>
  );
};

export default GroupCompanyUserAdd;
